import { computed, ref } from 'vue';
import { acceptHMRUpdate, defineStore, storeToRefs } from 'pinia';
import { merge, replace, template } from 'lodash-es';
import { marked } from 'marked';
import { useApiResponsesStore } from './responses';
import { useChooserFormStore } from './forms/chooser';
import { useTariffsStore } from '@/store/tariffs';

export const useTranslationsStore = defineStore('translations', () => {
    const formStore = useChooserFormStore();
    const { formData } = storeToRefs(formStore);

    const apiResponsesStore = useApiResponsesStore();
    const { countryResponses, regionResponses } =
        storeToRefs(apiResponsesStore);

    const tariffsStore = useTariffsStore();
    const { yearly, weekly } = storeToRefs(tariffsStore);

    const translations = ref({});
    const locale = ref(null);
    const languageCode = computed(() => {
        return replace(locale.value, '_', '-');
    });

    const currency = computed(() => {
        if (!regionResponses.value.annualLivingWages) {
            return undefined;
        }
        return regionResponses.value.annualLivingWages.data.currency;
    });

    const formatCurrency = computed(() => {
        return (value) => {
            let options = {
                style: 'currency',
                currency: currency.value,
            };
            // Decimals are always shown
            // if (showDecimals === undefined || showDecimals === false) {
            //     options.minimumFractionDigits = 0;
            //     options.maximumFractionDigits = 0;
            // }
            const formatter = new Intl.NumberFormat(
                languageCode.value,
                options
            );
            return formatter.format(value);
        };
    });
    const currencySymbol = computed(() => {
        const formatted = formatCurrency.value(0);
        return formatted.replace(/\d/g, '').trim();
    });

    const context = computed(() => {
        if (
            !regionResponses.value.annualLivingWages
        ) {
            return {
                yearnow: new Date().getFullYear(),
            };
        }

        const context = {
            region: formData.value.region.name,
            country: countryResponses.value.name,
            yearnow: yearly.value.current,
            actual_working_days: yearly.value.totalWorkDaysMinusDaysOff,
            weekly_workhours: weekly.value.workHours
        };

        if (formData.value.occupation.levelTwo) {
            context['job_title'] = formData.value.occupation.levelTwo.name;
        }
        return context;
    });

    const trans = computed(() => {
        return (identifier, isMarkdown, extraContext) => {
            let translation = translations.value[identifier];
            if (translation === undefined) {
                return identifier;
            }

            let value;
            let _context = context.value;
            if (extraContext !== undefined) {
                _context = merge(_context, extraContext);
            }

            try {
                const compiled = template(translation);
                value = compiled(_context);
            } catch (error) {
                console.error({
                    error,
                    identifier,
                    _context,
                    translation,
                });
                value = translation;
            }

            if (isMarkdown) {
                return marked
                    .parse(value, { mangle: false, headerIds: false })
                    .replace(/<\/?p>/g, '');
            }
            return value;
        };
    });

    const isReady = computed(() => {
        const { countryResponses } = storeToRefs(apiResponsesStore);

        return Boolean(translations.value && countryResponses.value.name);
    });

    return {
        locale,
        languageCode,
        currency,
        context,
        translations,
        trans,
        isReady,
        formatCurrency,
        currencySymbol,
    };
});

if (import.meta.hot) {
    import.meta.hot.accept(
        acceptHMRUpdate(useTranslationsStore, import.meta.hot)
    );
}
